import { sendTelemetryPayload, TelemetryTransportError } from './backend'

export type StoredReport = { id: string; endpoint: string; body: string; createdAt: number; critical: boolean }
export const OUTBOX_LIMITS = { packets: 32, bytes: 256_000, ttlMs: 24 * 60 * 60_000 } as const

export function retainReports(rows: StoredReport[], now: number): StoredReport[] {
  let bytes = 0
  return rows.filter(row => typeof row?.body === 'string' && row.body.length <= 48_000
    && typeof row.id === 'string' && typeof row.endpoint === 'string'
    && Number.isFinite(row.createdAt) && now - row.createdAt < OUTBOX_LIMITS.ttlMs && row.createdAt <= now)
    .sort((a, b) => Number(b.critical) - Number(a.critical) || b.createdAt - a.createdAt)
    .filter((row, index) => {
      const size = new Blob([row.body]).size
      if (index >= OUTBOX_LIMITS.packets || bytes + size > OUTBOX_LIMITS.bytes) return false
      bytes += size
      return true
    })
}

let database: Promise<IDBDatabase | undefined> | undefined
function openDatabase(): Promise<IDBDatabase | undefined> {
  if (typeof indexedDB === 'undefined') return Promise.resolve(undefined)
  return database ??= new Promise(resolve => {
    let settled = false
    const finish = (db?: IDBDatabase) => {
      if (settled) { db?.close(); return }
      settled = true
      clearTimeout(timer)
      resolve(db)
    }
    const timer = setTimeout(() => finish(), 500)
    try {
      const request = indexedDB.open('xmax-probe-outbox', 1)
      request.onupgradeneeded = () => request.result.createObjectStore('reports', { keyPath: 'id' })
      request.onsuccess = () => {
        request.result.onversionchange = () => { request.result.close(); database = undefined }
        finish(request.result)
      }
      request.onerror = request.onblocked = () => finish()
    } catch { finish() }
  })
}

async function transaction<T>(run: (store: IDBObjectStore, done: (value: T) => void) => void): Promise<T | undefined> {
  const db = await openDatabase()
  if (!db) return
  return new Promise(resolve => {
    let result: T | undefined
    let tx: IDBTransaction | undefined
    const timer = setTimeout(() => { try { tx?.abort() } catch {} resolve(undefined) }, 500)
    const finish = (value?: T) => { clearTimeout(timer); resolve(value) }
    try {
      tx = db.transaction('reports', 'readwrite')
      tx.oncomplete = () => finish(result)
      tx.onerror = tx.onabort = () => finish()
      run(tx.objectStore('reports'), value => { result = value })
    } catch { finish() }
  })
}

export const reportStore = {
  put: (row: StoredReport) => transaction<void>((store, done) => {
    const request = store.getAll()
    request.onsuccess = () => {
      const existing = request.result as StoredReport[]
      const retained = retainReports([...existing.filter(item => item.id !== row.id), row], Date.now())
      const ids = new Set(retained.map(item => item.id))
      existing.forEach(item => { if (!ids.has(item.id)) store.delete(item.id) })
      if (ids.has(row.id)) store.put(row)
      done()
    }
  }),
  remove: (id: string) => transaction<void>((store, done) => { store.delete(id); done() }),
  clear: () => transaction<void>((store, done) => { store.clear(); done() }),
  list: () => transaction<StoredReport[]>((store, done) => {
    const request = store.getAll()
    request.onsuccess = () => {
      const existing = request.result as StoredReport[]
      const retained = retainReports(existing, Date.now())
      const ids = new Set(retained.map(item => item.id))
      existing.forEach(item => { if (!ids.has(item.id)) store.delete(item.id) })
      done(retained.sort((a, b) => a.createdAt - b.createdAt))
    }
  }),
}

const active = new Set<string>()
let endpoint: string | undefined
let epoch = 0
let replaying = false
let timer: ReturnType<typeof setTimeout> | undefined

export function configureReportOutbox(target?: string, clear = false): void {
  if (endpoint === target && !clear) return
  epoch++
  endpoint = target
  active.clear()
  clearTimeout(timer)
  timer = undefined
  if (clear) void reportStore.clear()
  if (target) wakeReportOutbox()
}

export function persistReport(row: StoredReport): void {
  if (!endpoint || row.endpoint !== endpoint) return
  active.add(row.id)
  void reportStore.put(row)
}

export function acknowledgeReport(id: string): void {
  active.delete(id)
  void reportStore.remove(id)
}

export function releaseReport(id: string): void {
  active.delete(id)
  wakeReportOutbox(30_000)
}

export function wakeReportOutbox(delay = 0): void {
  if (!endpoint || timer || replaying || typeof indexedDB === 'undefined') return
  timer = setTimeout(() => { timer = undefined; void replay() }, delay)
}

async function replay(): Promise<void> {
  if (replaying || !endpoint) return
  replaying = true
  const run = epoch
  const target = endpoint
  let retry = false
  let retryDelay = 2_000
  try {
    const rows = await reportStore.list() ?? []
    for (const row of rows) {
      if (run !== epoch) break
      if (row.endpoint !== target || active.has(row.id)) continue
      if (typeof navigator !== 'undefined' && navigator.onLine === false) { retry = true; retryDelay = 30_000; break }
      try {
        await sendTelemetryPayload(target, row.body, 3000)
        await reportStore.remove(row.id)
      } catch (error) {
        if (error instanceof TelemetryTransportError && !error.retryable) {
          await reportStore.remove(row.id)
        } else { retry = true; retryDelay = 30_000; break }
      }
      // Drain one cached packet per tick; never flood a newly recovered connection.
      retry = true
      break
    }
  } catch { /* Storage and replay cannot affect the active Session. */ }
  finally {
    replaying = false
    if (run !== epoch) wakeReportOutbox()
    else if (retry) wakeReportOutbox(retryDelay)
  }
}
